/**
 * Clôture mensuelle des saisies de temps.
 *
 * Le verrou est un SCALAIRE, pas un drapeau par saisie : le document
 * `settings/timeLock` porte `lockedThrough: 'YYYY-MM'`, et tout mois
 * inférieur ou égal est gelé. Figer six ans d'historique revient donc à
 * écrire une chaîne — aucune migration sur les dizaines de milliers de
 * `timeEntries` existantes.
 *
 * Ce module est partagé exprès : `kronos-api` applique le verrou et le
 * client le reflète (lignes en lecture seule, bandeau, case du modal
 * d'export). S'ils calculaient le mois séparément ils divergeraient sur
 * la frontière de minuit, et l'UI proposerait des lignes que le serveur
 * refuse.
 */

/** Altyor saisit en heure locale française ; le mois civil se calcule là. */
export const TIME_LOCK_TZ = 'Europe/Paris';

/** Chemin du document unique portant le verrou. */
export const TIME_LOCK_DOC_PATH = 'settings/timeLock';
export const TIME_LOCK_COLLECTION = 'settings';
export const TIME_LOCK_DOC_ID = 'timeLock';

export interface TimeLock {
  /** 'YYYY-MM' : dernier mois gelé (inclus). `null` = rien n'est gelé. */
  lockedThrough: string | null;
  updatedBy?: string;
}

/**
 * Mois civil parisien d'un instant, en 'YYYY-MM'.
 *
 * `fr-CA` est la locale ISO (`YYYY-MM-DD`) — le seul moyen d'obtenir une
 * date civile dans un autre fuseau sans dépendance ni arithmétique de
 * décalage à la main (l'heure d'été change le décalage deux fois par an).
 */
export function monthKey(d: Date): string {
  return d.toLocaleDateString('fr-CA', { timeZone: TIME_LOCK_TZ }).slice(0, 7);
}

/** 'YYYY-MM' étant zéro-padé, la comparaison lexicale ordonne bien année puis mois. */
export function isMonthKeyLocked(key: string, lockedThrough: string | null | undefined): boolean {
  if (!lockedThrough) return false;
  return key <= lockedThrough;
}

/** Vrai si la saisie tombe dans un mois clôturé. */
export function isDateLocked(d: Date, lockedThrough: string | null | undefined): boolean {
  return isMonthKeyLocked(monthKey(d), lockedThrough);
}

/** Le mois précédent — ce que le modal d'export propose de figer. */
export function previousMonthKey(now: Date): string {
  const [y, m] = monthKey(now).split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Garde de frontière : un `lockedThrough` reçu d'un client doit avoir cette forme. */
export function isValidMonthKey(v: unknown): boolean {
  return typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
}
